/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * `protectEn` — MỘT BYTE MANG HAI THỨ KHÁC NHAU, và đó là chỗ dễ xoá mất nhau nhất.
 *
 *     bit 0  dòng rò      bit 1  quá áp      bit 2  thấp áp      bit 3  quá dòng
 *     bit 4, 5            còn trống
 *     bit 6-7             SỐ PHA CỦA TỦ  (0 = CHƯA ĐẶT, không phải "0 pha")
 *
 * Nguồn: `dev_config_para.h` — `PROTECT_BIT_CO_NGHIA = 0xCF`, `PROTECT_SO_PHA_MASK = 0xC0`.
 *
 * 🔴 CÁI TÊN NÓI SAI, và firmware đã ghi thẳng điều đó ra: *"`protect_en` KHÔNG phải cờ
 *   'cho phép bảo vệ' cho bit 6-7. Ai đọc tên trường mà không đọc chú thích sẽ tưởng bit
 *   6-7 là hai lớp bảo vệ nữa."*
 *
 * ⛔ VÀ ĐÂY LÀ CÁI BẪY THẬT, đã cắn firmware ở HAI chỗ (`main.cpp:1276` đường màn hình,
 *   `kapp_v2_app.cpp:911` đường app): mặt nạ `& 0x0F` viết từ hồi byte này chỉ có 4 bit.
 *   Đặt số pha xong, lần ghi cấu hình kế tiếp **xoá sạch nó — IM LẶNG**, giá trị chỉ lặng
 *   lẽ về 0. dev_device đã sửa cả hai bên tủ.
 *
 * ⇒ Bên app KHÔNG được đẻ ra chỗ thứ ba. Mọi phép sửa `protectEn` phải đi qua đây, và mỗi
 *   hàm dưới đây chỉ đụng đúng phần của nó, giữ nguyên phần kia. Đó là lý do file này tồn
 *   tại thay vì vài phép `| (1 << bit)` rải trong JSX.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */

export const SO_PHA_DICH = 6;
export const MAT_NA_SO_PHA = 0xc0;
export const MAT_NA_LOP = 0x0f;
/** bit0..3 bảo vệ + bit6..7 số pha. Bit 4,5 còn trống — `dev_config_para.h`. */
export const BIT_CO_NGHIA = 0xcf;

/** `0` = CHƯA ĐẶT. Máy chủ đọc ra 0 thì là CHƯA RÕ: không dựng `PHASE_LOSS`, cũng không đóng nó (L17). */
export type SoPha = 0 | 1 | 2 | 3;

export const NHAN_SO_PHA: Record<SoPha, string> = {
  0: 'Chưa đặt',
  1: '1 pha',
  2: '2 pha',
  3: '3 pha',
};

/** `true` khi số pha chưa được ai đặt — giao diện hiện xám nghiêng, KHÔNG hiện "0 pha". */
export const chuaDatSoPha = (n: SoPha) => n === 0;

export function docSoPha(protectEn: number | undefined | null): SoPha {
  const v = Number(protectEn ?? 0);
  if (!Number.isFinite(v)) return 0;
  return (((v & MAT_NA_SO_PHA) >>> SO_PHA_DICH) & 0x03) as SoPha;
}

/**
 * ⛔ GIỮ NGUYÊN bit 0..3. Đây chính là chỗ firmware từng xoá mất cấu hình bảo vệ khi đi
 *   chiều ngược lại — cùng một cái bẫy, hai đầu.
 */
export function datSoPha(protectEn: number | undefined | null, soPha: SoPha): number {
  const v = Number(protectEn ?? 0);
  const goc = Number.isFinite(v) ? v : 0;
  return ((goc & ~MAT_NA_SO_PHA) | ((soPha & 0x03) << SO_PHA_DICH)) & 0xff;
}

/** ⛔ GIỮ NGUYÊN bit 6..7. Chiều còn lại của đúng cái bẫy trên. */
export function datLop(protectEn: number | undefined | null, bit: number, bat: boolean): number {
  const v = Number(protectEn ?? 0);
  const goc = Number.isFinite(v) ? v : 0;
  return (bat ? goc | (1 << bit) : goc & ~(1 << bit)) & 0xff;
}

export const lopDangBat = (protectEn: number | undefined | null, bit: number) =>
  ((Number(protectEn ?? 0) >>> bit) & 1) === 1;

/* ─────────────────────────────────────────────────────────── dải "lớp đang TẮT" */

/**
 * NGƯỠNG XUẤT XƯỞNG — đọc từ `dev_config_para.h`, KHÔNG phải số tôi nghĩ ra:
 *   `DEF_OV_SET 270` · `DEF_UV_SET 170` · `DEF_OC_SET 50` · `DEF_EL_SET 30`.
 *
 * 🔴 Ba con số này bác một tiền đề: dev_device chọn luật *"chỉ kêu khi NGƯỠNG ĐÃ ĐẶT mà lớp
 *   vẫn tắt"* để tủ mới đừng vàng — nhưng tủ xuất xưởng có `protectEn = 0x00` **VÀ** ngưỡng
 *   đã khác 0 sẵn. Nên luật ấy **vẫn vàng ở mọi tủ mới**, đúng thứ nó định tránh.
 *
 * ⇒ Dấu hiệu "quên bật" phải là **ngưỡng KHÁC MẶC ĐỊNH** — tức là có người đã đụng vào nó —
 *   chứ không phải "ngưỡng khác 0".
 */
export const NGUONG_XUAT_XUONG: Record<string, number> = {
  ovSetV: 270,
  uvSetV: 170,
  ocSetA: 50,
  elSetMa: 30,
};

export interface LopBaoVe {
  bit: number;
  ten: string;
  truongNguong: string;
}

/** Đúng thứ tự bit của `dev_config_para.h`. */
export const LOP_BAO_VE_BIT: LopBaoVe[] = [
  { bit: 0, ten: 'Dòng rò',  truongNguong: 'elSetMa' },
  { bit: 1, ten: 'Quá áp',   truongNguong: 'ovSetV' },
  { bit: 2, ten: 'Thấp áp',  truongNguong: 'uvSetV' },
  { bit: 3, ten: 'Quá dòng', truongNguong: 'ocSetA' },
];

export type LyDoTat = 'nguoi-da-dat-nguong' | 'con-mac-dinh';

export interface LopTat {
  bit: number;
  ten: string;
  nguong: number;
  /** `nguoi-da-dat-nguong` = có người sửa ngưỡng rồi quên bật lớp — đây mới là tín hiệu. */
  lyDo: LyDoTat;
}

/**
 * Những lớp ĐANG TẮT, kèm lý do phân biệt được *"quên bật"* với *"tủ vừa ra khỏi hộp"*.
 *
 * ⚠ Hàm này KHÔNG quyết định hiện dải hay không — nó chỉ trả sự thật. Chỗ quyết định là
 *   giao diện, và nó phải quyết bằng `lyDo`, không bằng số lượng.
 */
/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * 🔴 KHÔNG LỚP NÀO KHAI ĐƯỢC LÀ "TẮT" TỪ `protectEn` — YC-50 vòng 3 (dev_device 08/09).
 *
 * Lượt trước tôi viết ở ĐÚNG chỗ này: *"bit 0 và bit 3 KHÔNG bị luật OR chạm tới ⇒ vẫn khai
 * được"*. **Bit 3 BỊ chạm.** Lớp quá dòng nằm ở một HÀM KHÁC, cách chỗ hai lớp áp 200 dòng,
 * và có ĐỦ BỐN TẦNG y hệt:
 *
 *     bsp_dev_util.cpp:367   co_gan_dong_ho_oc = (modbus_model != modbus_none)
 *     bsp_dev_util.cpp:368   bat = ((protect_en & 0x08) || co_gan_dong_ho_oc) ? 1 : 0
 *     bsp_dev_util.cpp:382   if (bat && (oc_set < 1 || oc_set > 630))  bat = 0;
 *
 * ⛔ CÁI SAI THẬT KHÔNG PHẢI "THIẾU MỘT MỤC" — LÀ CHIỀU MẶC ĐỊNH CỦA DANH SÁCH.
 *   `BIT_TU_VU_TRANG` cũ là một danh sách CẤM: bit nào tôi ĐÃ THẤY có cổng thì cấm, còn lại
 *   mặc nhiên khai được. Tức là **"chưa ai chỉ cho tôi cái cổng" được tính thành "không có
 *   cổng"** — suy từ VẮNG MẶT BẰNG CHỨNG. Đúng thứ vừa sai hai lần trong hai ngày.
 *
 * ⇒ Đảo chiều mặc định. Nay là danh sách CHO PHÉP, và một bit chỉ vào được khi có người ĐỌC
 *   MÃ FIRMWARE và khẳng định KHÔNG cổng nào chạm nó (`hang: 'khong-cong'`). Danh sách ấy
 *   **sinh ra từ bảng cơ sở bên dưới**, không gõ tay — để nó không lệch khỏi lý do của nó.
 *
 * 🔴 VÒNG 4 — BIT 0 KHÔNG "CHƯA AI TRUY" NỮA, NÓ LÀ MỘT **BIT CHẾT** (dev_device đo 08/09):
 *
 *       $ grep -rn "protect_en & 0x01" lib/ src/      →  0 dòng
 *
 *   Firmware **không bao giờ đọc** `protectEn` bit 0. Đường vũ trang thật của chống rò nằm ở
 *   một LỚP KHÁC, và ELR-19N cắt bằng mạch phần cứng (`06_THIET_BI_ELR19N.md §3.1`):
 *
 *       bsp_modbus.cpp:328   if (elr_model == 0 || modbus == nullptr) return;   ← có lắp ELR không
 *       bsp_modbus.cpp:489   return (el_set != 0);                              ← 0 = TẮT chống rò
 *
 *   ⇒ Vũ trang dòng rò = `elrModel != 0 && elSetMa != 0` — **hai trường đã có sẵn trên dây**.
 *
 * ⛔ NHƯNG APP KHÔNG TỰ TÍNH LẠI PHÉP ẤY. Đó đúng là bệnh *"hai chỗ tính một quyết định"* đã
 *   bị cấm ba lần. Tủ tính, app đọc — `protectEnEffective` bit 0. Chờ, không tự suy.
 *
 * ⛔ VÀ NÓ PHƠI RA MỘT NÚT GẠT GIẢ TRONG GIAO DIỆN CỦA TÔI: hàng "Dòng rò" có công tắc ghi
 *   `protectEn` bit 0 — người dùng gạt, app lưu, **tủ không đọc**. Cùng hạng với `inputEn` /
 *   `outputEn` mà dev_device đã bắt dừng UI. Nút ấy nay KHÔNG được vẽ nữa.
 *
 * ⏳ `DeviceSettings.protectEnEffective` (tủ tự tính) là đường DUY NHẤT trả lại câu trả lời —
 *   nay **BỐN bit** (dev_device sửa hình dạng sau khi tôi báo lỗ hổng), nên ngày trường về là
 *   cả bốn lớp cùng sống lại, không lớp nào bị bỏ quên.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export type HangKhaiLop =
  /** Đã truy mã, KHÔNG cổng nào chạm ⇒ bit `protectEn` một mình trả lời được. */
  | 'khong-cong'
  /** Đã truy mã, CÓ cổng ⇒ bit một mình không trả lời được. */
  | 'co-cong'
  /** Firmware KHÔNG BAO GIỜ đọc bit này ⇒ không trả lời được, và mọi nút gạt vào nó là GIẢ. */
  | 'bit-chet'
  /** Chưa ai đọc mã ⇒ không trả lời được, nhưng mở lại được bằng MỘT dòng trích dẫn. */
  | 'chua-truy';

export interface CoSoKhaiLop {
  bit: number;
  hang: HangKhaiLop;
  /**
   * Điều kiện của những cổng firmware chạm tới bit này. Rỗng **KHÔNG** nghĩa là "không có
   * cổng" — chiều đó chỉ `hang` nói được.
   */
  cong: string[];
  /** Với `bit-chet`: đường vũ trang THẬT, để không ai tưởng lớp ấy không tồn tại. */
  duongThat?: string;
  ghiChu: string;
}

/**
 * ⛔ Bảng này là LÝ DO, không phải chú thích. `BIT_KHAI_DUOC` sinh ra từ nó.
 */
export const CO_SO_KHAI_LOP: CoSoKhaiLop[] = [
  {
    bit: 0,
    hang: 'bit-chet',
    cong: [],
    duongThat: 'elr_model != 0 && el_set != 0 — `bsp_modbus.cpp:328,489`; ELR-19N cắt bằng phần cứng',
    ghiChu:
      'Dòng rò — BIT CHẾT. `grep -rn "protect_en & 0x01"` ra 0 dòng: firmware không bao giờ '
      + 'đọc bit này. Lớp chống rò CÓ THẬT và đang chạy, chỉ là nó vũ trang bằng hai trường '
      + 'khác. ⛔ App KHÔNG tự tính lại `elrModel != 0 && elSetMa != 0` — tủ tính, app đọc.',
  },
  {
    bit: 1,
    hang: 'co-cong',
    cong: ['(protect_en & 0x02) || co_gan_dong_ho', 'ov_set < 100 || ov_set > 300',
           'bat_qua_ap && bat_thap_ap && uv_set >= ov_set'],
    ghiChu: 'Quá áp — OR theo đồng hồ, rồi hai cổng gỡ vũ trang. `bsp_dev_util.cpp:146-151,170`.',
  },
  {
    bit: 2,
    hang: 'co-cong',
    cong: ['(protect_en & 0x04) || co_gan_dong_ho', 'uv_set < 80 || uv_set > 250',
           'bat_qua_ap && bat_thap_ap && uv_set >= ov_set'],
    ghiChu: 'Thấp áp — cùng bốn tầng với quá áp. `bsp_dev_util.cpp:146-148,160,170`.',
  },
  {
    bit: 3,
    hang: 'co-cong',
    cong: ['(protect_en & 0x08) || co_gan_dong_ho_oc', 'oc_set < 1 || oc_set > 630'],
    ghiChu: 'Quá dòng — ĐỦ BỐN TẦNG, ở một hàm khác. `bsp_dev_util.cpp:367-368,382`.',
  },
];

/**
 * Những bit `protectEn` **một mình trả lời được**: đã truy mã, VÀ không cổng nào chạm.
 *
 * ⚠ Hôm nay danh sách này RỖNG, và rỗng vì **cấu tạo** — ba bit đã truy đều có cổng, bit còn
 *   lại là bit chết. Không phải một hằng số rỗng gõ vào.
 */
export const BIT_KHAI_DUOC: readonly number[] =
  CO_SO_KHAI_LOP.filter((c) => c.hang === 'khong-cong').map((c) => c.bit);

/**
 * ⛔ Những bit `protectEn` firmware KHÔNG BAO GIỜ đọc. Giao diện **không được vẽ công tắc**
 *   cho chúng — một nút gạt lưu được mà không tới dây là nói dối bằng hành vi, không bằng chữ.
 */
export const BIT_CHET_TRONG_PROTECT_EN: readonly number[] =
  CO_SO_KHAI_LOP.filter((c) => c.hang === 'bit-chet').map((c) => c.bit);

/**
 * 🔴 Những bit CHẾT đang được BẬT trong giá trị tủ khai — tức **dấu vết của kẻ đã đặt**.
 *
 * dev_device chốt 08/09: firmware **không cắt** bit chết khi ghi
 * (`kapp_v2_app.cpp:1178` dùng mặt nạ `0xCF`, bit0 lọt qua), *"cắt thì mất luôn dấu vết kẻ đã
 * đặt nó"*. ⇒ Bit ấy nằm lại trong NVS và dội về nguyên vẹn. Vòng phản hồi hoàn hảo quanh một
 * bit chết — và đó là lý do app KHÔNG THỂ tự phát hiện nút gạt giả của mình.
 *
 * ⚠ Nay app đọc được dấu vết ấy. Đo trong log lee (37 tệp, 224 lần tủ khai `protectEn`):
 *   `0x00`·`0xC0`·`0x42`·`0xC2` — **bit0 = 0 ở cả 224 lần**. Nút giả chưa từng được bấm.
 *   Nhưng màn hình HMI cũng ghi `protect_en` (`main.cpp:1294`), nên ca ấy còn ở phía trước.
 *
 * ⛔ App CHỈ BÁO, không sửa: xoá bit hộ là **xoá dấu vết**, và ghi `protectEn` là một LỆNH
 *   THẬT. Đây là câu về một SỐ ĐO app nhận được, không phải một hành động của tủ (YC-47).
 */
export function bitChetDangBat(protectEn: number | undefined | null): number[] {
  /*
   * ⚠ KHÔNG có chốt `Number.isFinite` ở đây, và đó là CÓ CHỦ Ý — phép phá đã chứng minh chốt
   *   ấy KHÔNG THỂ HỎNG: `NaN >>> b` là `0` theo ToUint32, nên bỏ chốt đi kết quả vẫn y hệt.
   *   Một chốt trông như đang gác mà không gác được gì là cùng họ "bộ đo không thể hỏng" —
   *   nó dạy người đọc sau tin vào một lớp bảo vệ không tồn tại. Thay bằng phép canh nêu
   *   thẳng tính chất: `undefined` · `NaN` · chuỗi rác đều ra `[]`.
   */
  const v = Number(protectEn ?? 0);
  return BIT_CHET_TRONG_PROTECT_EN.filter((b) => ((v >>> b) & 1) === 1);
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * 🔴 `protectEnEffective` — **HAI CÂU HỎI KHÁC NHAU, TRƯỚC HÔM NAY GỘP TRONG MỘT MẢNG.**
 *
 * dev_device đính chính 09/09 (bản cuối, thay công thức đưa buổi sáng):
 *
 *     hieu_luc = (pe & ~0x0F)   // ~0x0F = 0xF0 → XOÁ 0,1,2,3 rồi TÍNH LẠI CẢ BỐN · giữ 4,5,6,7
 *
 * ⇒ Trường ấy **mang** bit `0,1,2,3,6,7`. Nhưng *"mang bit X"* và *"trả lời được câu hỏi về
 *   lớp X"* là **hai chuyện khác nhau**, và mảng cũ `[0,1,2,3]` trộn cả hai:
 *
 *      bit 1,2,3   TÍNH LẠI theo tầng arm  →  TRẢ LỜI câu "lớp ấy CÓ VŨ TRANG không"
 *      bit 6,7     ĐI THẲNG (số pha)       →  không phải lớp bảo vệ, không có gì để trả lời
 *      bit 0       TÍNH LẠI                →  TRẢ LỜI MỘT CÂU **KHÁC** — xem khối ngay dưới
 *
 * ⛔ Vì sao tách: một mảng gộp khiến `BIT_TRONG_PROTECT_EN_EFFECTIVE.includes(6)` đọc ra
 *   *"lớp 6 trả lời được"* — mà **không có lớp 6 nào cả**. Đúng bệnh một cái tên hứa một phép
 *   đo chưa từng xảy ra.
 *
 * ✅ Và ghi lại lý do dev_device GIỮ bit 6-7 trong Effective thay vì bóc ra (nghe sạch hơn):
 *   bóc ra thì Effective có bit 6-7 = `0`, ai lỡ đọc số pha từ đó sẽ nhận `0` = *"chưa đặt"* —
 *   **một câu trả lời sai mà trông hợp lệ**, nguy hơn hẳn việc mang thừa hai bit. Đó đúng là
 *   luật số 0 của chính dự án này, áp ở một chỗ tôi không nghĩ tới.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export type HangBitEffective =
  /** Tủ TÍNH LẠI ⇒ bit này mang một giá trị tủ tự suy ra, không phải bản sao cấu hình. */
  | 'tinh-lai'
  /** Sao chép nguyên từ `protectEn` ⇒ nó nói về CẤU HÌNH, không nói về hiệu lực. */
  | 'di-thang';

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * 🔴 `tinh-lai` KHÔNG ĐỦ ĐỂ BIẾT ĐƯỢC PHÉP KẾT LUẬN GÌ — 09/09 lượt 45.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * Hôm nay dev_device trả lời bit 0: **TÍNH LẠI**, mặt nạ `& ~0x0F`. Phản xạ đầu tiên của tôi
 * là dồn nó vào `BIT_TRA_LOI_DUOC_O_EFFECTIVE` cùng 1,2,3 và tuyên bố *"hết cấm, bốn lớp trả
 * lời được"*. **Sai — và sai đúng cái kiểu tôi vừa sửa ba ngày trước.**
 *
 * Nguyên văn dev_device: *"bit0 = 1 ⟺ tủ đang THEO DÕI rò điện (`elr_model != 0` và
 * `el_set != 0`). ⚠️ Không đọc thành 'bảo vệ rò đang có hiệu lực' — ELR-19N cắt bằng mạch
 * riêng của nó, không qua tủ."*
 *
 *      bit 1,2,3  →  "lop ay CO VU TRANG khong"     ← tu vua biet vua CAT duoc
 *      bit 0      →  "tu CO DANG THEO DOI khong"    ← tu biet, nhung KHONG cat
 *
 * ⇒ Hai câu **khác nhau**, và câu thứ hai **yếu hơn hẳn**. Tủ theo dõi rò không có nghĩa dòng
 *   rò sẽ được cắt — việc cắt nằm ở ELR-19N, một mạch app không nhìn thấy và tủ không điều
 *   khiển. Gộp hai câu là hứa một lớp an toàn mà **không ai trong hệ thống này hứa được**.
 *
 * 📌 Nên `hang` (tủ tính hay tủ chép) **không** quyết được ta kết luận gì. Phải hỏi thêm:
 *   **bit này trả lời CÂU NÀO.** Trước hôm nay hai thứ ấy trùng nhau nên một trường là đủ;
 *   câu trả lời của dev_device là chỗ chúng tách ra.
 */
export type CauBitTraLoi =
  /** "Lớp ấy có đang vũ trang không" — tủ biết VÀ tủ cắt. Kết luận được về bảo vệ. */
  | 'lop-dang-vu-trang'
  /** "Tủ có đang theo dõi không" — tủ biết, nhưng KHÔNG cắt. ⛔ Không kết luận được về bảo vệ. */
  | 'tu-dang-theo-doi'
  /** Không nói gì về bảo vệ (số pha). */
  | 'khong-noi-ve-bao-ve';

export interface CoSoBitEffective {
  bit: number;
  hang: HangBitEffective;
  cauTraLoi: CauBitTraLoi;
  ghiChu: string;
}

/** ⛔ Bảng này là LÝ DO. Hai mảng dưới SINH RA từ nó, không gõ tay. */
export const CO_SO_BIT_EFFECTIVE: CoSoBitEffective[] = [
  {
    bit: 0,
    hang: 'tinh-lai',
    cauTraLoi: 'tu-dang-theo-doi',
    ghiChu:
      '✅ dev_device trả lời 09/09: TÍNH LẠI, mặt nạ `& ~0x0F` (không phải `~0x0E`). '
      + '`bit0 = 1` ⟺ tủ đang THEO DÕI rò (`elr_model != 0 && el_set != 0`). '
      + '⛔ KHÔNG đọc thành "bảo vệ rò đang có hiệu lực": ELR-19N cắt bằng MẠCH RIÊNG của nó, '
      + 'không qua tủ. Tủ biết, tủ KHÔNG cắt ⇒ đây là câu YẾU HƠN HẲN ba bit kia. '
      + '📌 Lỗ hổng cũ (bit 0 của `protectEn` là bit CHẾT) VẪN ĐÚNG và không bị câu này xoá — '
      + 'hai trường khác nhau: `protectEn` chép cấu hình, `protectEnEffective` tủ tự tính.',
  },
  { bit: 1, hang: 'tinh-lai', cauTraLoi: 'lop-dang-vu-trang', ghiChu: 'Quá áp — tủ tính lại theo tầng arm, và tủ CẮT được.' },
  { bit: 2, hang: 'tinh-lai', cauTraLoi: 'lop-dang-vu-trang', ghiChu: 'Thấp áp — tủ tính lại theo tầng arm, và tủ CẮT được.' },
  { bit: 3, hang: 'tinh-lai', cauTraLoi: 'lop-dang-vu-trang', ghiChu: 'Quá dòng — tủ tính lại theo tầng arm, và tủ CẮT được.' },
  {
    bit: 6,
    hang: 'di-thang',
    cauTraLoi: 'khong-noi-ve-bao-ve',
    ghiChu: 'SỐ PHA (bit thấp) — không phải lớp bảo vệ. Giữ trong Effective có chủ ý: bóc ra '
      + 'thì ai đọc số pha từ đây sẽ nhận 0 = "chưa đặt", một câu sai mà trông hợp lệ.',
  },
  {
    bit: 7,
    hang: 'di-thang',
    cauTraLoi: 'khong-noi-ve-bao-ve',
    ghiChu: 'SỐ PHA (bit cao) — như bit 6.',
  },
];

/**
 * 🔴 MẶT NẠ SINH RA TỪ BẢNG, không gõ tay — và có phép canh ghép nó với con số dev_device đưa.
 *
 * `& ~0x0F` nghĩa là *"xoá bốn bit thấp rồi tính lại cả bốn"*. Bốn bit ấy đúng bằng những bit
 * xếp `tinh-lai` trong bảng trên. Gõ `0x0F` thành một hằng số rời là để hai thứ trôi khỏi nhau
 * trong im lặng — đúng bệnh `TEN_LENH` gõ song song, bảng vai gõ tay, `lam_lai` gõ tay.
 */
export const MAT_NA_TINH_LAI_EFFECTIVE: number =
  CO_SO_BIT_EFFECTIVE.filter((c) => c.hang === 'tinh-lai')
    .reduce((m, c) => m | (1 << c.bit), 0);

/** Mọi bit trường ấy MANG — kể cả bit không nói gì về vũ trang. */
export const BIT_TRONG_PROTECT_EN_EFFECTIVE: readonly number[] =
  CO_SO_BIT_EFFECTIVE.map((c) => c.bit);

/**
 * 🔴 Những bit TRẢ LỜI ĐƯỢC câu *"lớp này có đang vũ trang không"*.
 * ⛔ Đây mới là mảng được phép dùng để kết luận. `BIT_TRONG_PROTECT_EN_EFFECTIVE` chỉ nói
 *   trường ấy chở gì.
 */
export const BIT_TRA_LOI_DUOC_O_EFFECTIVE: readonly number[] =
  CO_SO_BIT_EFFECTIVE.filter((c) => c.cauTraLoi === 'lop-dang-vu-trang').map((c) => c.bit);

/**
 * 🔴 Bit nói *"tủ ĐANG THEO DÕI"* — biết, nhưng **KHÔNG cắt**. Mảng riêng, tên riêng.
 *
 * ⛔ Đây KHÔNG phải một loại "trả lời được" hạng hai để rồi ai đó gộp vào cho gọn. Nó là một
 *   CÂU KHÁC. Gộp là hứa một lớp an toàn mà không bộ phận nào trong hệ này hứa được: việc cắt
 *   dòng rò nằm ở ELR-19N, một mạch tủ không điều khiển và app không nhìn thấy.
 */
export const BIT_NOI_TU_DANG_THEO_DOI: readonly number[] =
  CO_SO_BIT_EFFECTIVE.filter((c) => c.cauTraLoi === 'tu-dang-theo-doi').map((c) => c.bit);

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * 🔴 NHÃN CHO BIT 0 — viết sẵn ở đây để **không ai phải tự nghĩ ra một câu**.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * Đây là chỗ lỗi sẽ xảy ra, và nó sẽ xảy ra ở một CÁI TÊN chứ không ở một dòng lô-gic. Ngày
 * ai đó nối `protectEnEffective` lên trình duyệt (`NỢ-P4-01`), câu tự nhiên nhất để viết cạnh
 * một bit `= 1` là *"Chống rò: BẬT"* — và câu ấy **hứa một hành động hệ thống không làm**,
 * đúng hạng `YC-47`. Tủ theo dõi; ELR-19N mới là thứ cắt.
 *
 * ⇒ Hàm này là câu DUY NHẤT được phép, và có phép canh cấm mấy câu kia.
 */
export const nhanDongRoHieuLuc = (bit0: boolean): string =>
  bit0 ? 'Tủ đang theo dõi dòng rò' : 'Tủ KHÔNG theo dõi dòng rò';

/** Câu giải thích đi kèm — nói ra vế mà cái nhãn không chứa nổi. */
export const CAU_PHU_DONG_RO =
  'Việc CẮT khi có rò do ELR-19N tự làm bằng mạch riêng, không qua tủ — tủ chỉ theo dõi.';

/**
 * Những LỚP BẢO VỆ mà **kể cả sau khi `protectEnEffective` về** vẫn không trả lời được.
 *
 * 🔴 ĐỔI NGUỒN 09/09: lọc theo `BIT_TRA_LOI_DUOC_O_EFFECTIVE`, KHÔNG theo
 *   `BIT_TRONG_PROTECT_EN_EFFECTIVE`. Trường có CHỞ một bit không có nghĩa bit ấy trả lời
 *   được — và đúng bit 0 (dòng rò) là chỗ hai câu ấy tách ra.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ⚠️ 09/09 lượt 45 — MẢNG NÀY VẪN LÀ `[0]`, VÀ ĐÓ LÀ CHỖ DỄ ĐỌC NHẦM NHẤT CỦA CẢ TỆP.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * dev_device vừa trả lời bit 0: **TÍNH LẠI**, `& ~0x0F`. Nghe như tin tốt, và nó đúng là tin
 * tốt — nhưng mảng này **không đổi một phần tử nào**. Nhìn từ ngoài thì giống hệt "chưa có
 * câu trả lời". Thực ra **lý do đã thay hoàn toàn**:
 *
 *      truoc 09/09 sang : "chua biet bit 0 co nghia gi"            ← thieu THONG TIN
 *      sau  09/09 chieu : "biet chinh xac nghia, va nghia ay
 *                          KHONG PHAI cau ta dang hoi"             ← thieu THAM QUYEN
 *
 * Tủ **biết** nó đang theo dõi rò; tủ **không cắt** khi có rò. Câu *"lớp dòng rò có đang bảo
 * vệ không"* vẫn **không ai trong hệ này trả lời được** — ELR-19N cắt bằng mạch riêng, tủ
 * không điều khiển, app không nhìn thấy.
 *
 * ⛔ Nên đừng "sửa" mảng này cho rỗng khi thấy bit 0 đã `tinh-lai`. Rỗng vì đủ lớp thì tốt;
 *   rỗng vì gộp hai câu hỏi khác nhau thì đúng bằng việc khai một lớp an toàn không tồn tại.
 * 📌 *Một câu trả lời có thật vẫn có thể là câu trả lời cho một câu hỏi khác.*
 */
export const BIT_CON_CAM_SAU_KHI_CO_TRUONG: readonly number[] =
  LOP_BAO_VE_BIT.map((l) => l.bit).filter((b) => !BIT_TRA_LOI_DUOC_O_EFFECTIVE.includes(b));

/**
 * `false` ⇒ app **chưa kết luận được lớp nào TẮT**, và giao diện phải nói đúng câu ấy.
 *
 * ⛔ Đừng đọc `lopDangTat(...).length === 0` thay cho câu hỏi này: mảng rỗng trộn *"không lớp
 *   nào tắt"* với *"tôi không trả lời được"*. Hai câu ngược nhau, và câu sau in ra thành
 *   "0 lớp TẮT" thì người trực đọc thành "cả bốn lớp đang BẬT" — đúng chiều nguy.
 */
export const khaiDuocLopTat = () => BIT_KHAI_DUOC.length > 0;

export function lopDangTat(
  protectEn: number | undefined | null,
  nguong: Record<string, number | undefined>,
  /**
   * ⛔ CHỈ phép đo được truyền tham số này — để chứng minh guồng máy còn SỐNG khi danh sách
   *   thật rỗng. Mọi chỗ gọi trong ứng dụng phải bỏ trống. Có phép canh cấm.
   */
  bitKhaiDuoc: readonly number[] = BIT_KHAI_DUOC,
): LopTat[] {
  const ra: LopTat[] = [];
  for (const l of LOP_BAO_VE_BIT) {
    if (lopDangBat(protectEn, l.bit)) continue;
    // ⛔ "bit tắt" KHÔNG có nghĩa "lớp tắt". Chỉ bit ĐÃ TRUY NGUỒN và không cổng nào chạm.
    if (!bitKhaiDuoc.includes(l.bit)) continue;
    const v = Number(nguong[l.truongNguong] ?? 0) || 0;
    const mac = NGUONG_XUAT_XUONG[l.truongNguong];
    ra.push({
      bit: l.bit,
      ten: l.ten,
      nguong: v,
      lyDo: v !== 0 && v !== mac ? 'nguoi-da-dat-nguong' : 'con-mac-dinh',
    });
  }
  return ra;
}

/** `true` khi CÓ ÍT NHẤT MỘT lớp mà người ta đã sửa ngưỡng rồi quên bật. */
export const coDauHieuQuenBat = (ds: LopTat[]) => ds.some((l) => l.lyDo === 'nguoi-da-dat-nguong');
